import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Plus, MessageSquare, LayoutGrid, LogOut, ShieldAlert, Hammer } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import NotificationBell from "@/components/NotificationBell";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (location.hash?.includes("session_id=")) return null;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl backdrop-saturate-150 border-b border-black/5">
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-[#FF5A00] flex items-center justify-center">
            <Hammer className="w-5 h-5 text-white" />
          </div>
          <span className="font-display text-lg font-bold tracking-tight">SelLend</span>
        </Link>

        <div className="flex items-center gap-2 sm:gap-3">
          <Link
            to="/browse"
            data-testid="nav-browse"
            className="hidden sm:inline text-sm font-medium px-4 py-2 rounded-full hover:bg-black/5 transition-colors"
          >
            Browse
          </Link>
          <Link
            to="/how-it-works"
            data-testid="nav-how-it-works"
            className="hidden md:inline text-sm font-medium px-4 py-2 rounded-full hover:bg-black/5 transition-colors"
          >
            How it works
          </Link>
          {user ? (
            <>
              <Link
                to="/create"
                data-testid="nav-list-item"
                className="flex items-center gap-1.5 text-sm font-medium bg-[#0A0A0A] text-white px-4 py-2 rounded-full hover:bg-[#FF5A00] transition-colors active:scale-95"
              >
                <Plus className="w-4 h-4" /> <span className="hidden sm:inline">List an item</span>
              </Link>
              <Link to="/messages" data-testid="nav-messages" className="p-2 rounded-full hover:bg-black/5 transition-colors">
                <MessageSquare className="w-5 h-5" />
              </Link>
              <NotificationBell />
              <DropdownMenu>
                <DropdownMenuTrigger data-testid="nav-user-menu" className="outline-none">
                  <Avatar className="w-9 h-9 border border-black/10">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback className="bg-[#E3E8E1] text-sm font-semibold">
                      {user.name?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-semibold truncate">{user.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")}>
                    <LayoutGrid className="w-4 h-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-messages" onClick={() => navigate("/messages")}>
                    <MessageSquare className="w-4 h-4 mr-2" /> Messages
                  </DropdownMenuItem>
                  {user.is_admin && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                      <ShieldAlert className="w-4 h-4 mr-2" /> Admin panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={handleLogout}>
                    <LogOut className="w-4 h-4 mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login"
              className="text-sm font-medium bg-[#0A0A0A] text-white px-5 py-2 rounded-full hover:bg-[#FF5A00] transition-colors active:scale-95"
            >
              Sign in
            </Link>
          )}
        </div>
      </nav>
    </header>
  );
}
